import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import moment from "moment";
import { Eye } from "lucide-react";
import { useLocation } from "react-router-dom";

import DocumentViewer from "../../../components/DocumentViewer.jsx";
import { API_ANNOUNCEMENT, API_GET_EMPLOYEE_FILE } from "../../../config/Endpoints.js";
import { getAnnouncementEventTypeOptions } from "../../../constants/announcementEventTypeOptions.js";
import { getApi } from "../../../utils/api.js";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import { Badge, Button, Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/index.ts";
import { ResponsiveDataTable, type ResponsiveColumn } from "../../components/ui/responsive-table/responsive-table.tsx";
import { useSqeLabs } from "../../hooks/use-sqe-labs.ts";
import { showError } from "../../utils/toast.ts";
import { announcementAttachmentName, announcementStatusLabel, announcementStatusVariant, type AnnouncementRow } from "./announcement-types.ts";
import styles from "./announcement-management.module.css";

type PageProps = { title?: string; description?: string };
type DocumentDetail = { url: string; type: string; name: string };

const REGULAR_SESSIONS = [
  { value: "Classroom Session", label: "Classroom Sessions" },
  { value: "Coffee & Learn Session", label: "Coffee & Learn Sessions" },
  { value: "MuleSoft Meetups", label: "MuleSoft Meetups" },
  { value: "Salesforce Session", label: "Salesforce Sessions" },
  { value: "Executive Education Session", label: "Executive Education Session" },
];

function attachmentContentType(path: string): string {
  const extension = announcementAttachmentName(path)?.split(".").pop()?.toLowerCase();
  if (extension === "png") return "image/png";
  if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
  return "application/pdf";
}

export default function ViewAnnouncementPage({ title, description }: PageProps) {
  const isSqeLabs = useSqeLabs();
  const { hash } = useLocation();
  const accessToken = Cookies.get("accessToken");
  const sessions = useMemo(
    () => isSqeLabs ? getAnnouncementEventTypeOptions(true) : REGULAR_SESSIONS,
    [isSqeLabs],
  );
  const [activeTabId, setActiveTabId] = useState("tab1");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRequest, setTotalRequest] = useState(0);
  const [rows, setRows] = useState<AnnouncementRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
  const [documentDetail, setDocumentDetail] = useState<DocumentDetail | null>(null);

  useEffect(() => {
    const requestedTab = hash.replace("#", "");
    const maxTab = sessions.length;
    if (/^tab[1-5]$/.test(requestedTab) && Number(requestedTab.slice(3)) <= maxTab) {
      setActiveTabId(requestedTab);
      setCurrentPage(1);
    }
  }, [hash, sessions]);

  const fetchList = useCallback(async () => {
    const index = Number(activeTabId.slice(3)) - 1;
    const selectedEventType = sessions[index]?.value;
    if (!selectedEventType) return;
    setIsLoading(true);
    try {
      const res = await getApi(
        `${API_ANNOUNCEMENT}?pageSize=${pageSize}&page=${currentPage}&status=APPROVED&eventType=${encodeURIComponent(selectedEventType)}`,
        accessToken,
      );
      if (res.status === 200) {
        const body = res?.data?.data;
        const nextRows = Array.isArray(body?.data) ? body.data : [];
        const totalCount = Number(body?.totalRequest ?? body?.totalPolicy ?? body?.totalElements ?? nextRows.length) || 0;
        setRows(nextRows);
        setTotalRequest(totalCount);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, activeTabId, currentPage, pageSize, sessions]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  useEffect(() => {
    if (documentDetail?.url) document.getElementById("open-document-view")?.click();
  }, [documentDetail?.url]);

  const viewAttachment = async (attachmentPath: string) => {
    const fileName = announcementAttachmentName(attachmentPath) ?? "Document";
    const fallbackType = attachmentContentType(attachmentPath);
    const filepath = attachmentPath.split("/").slice(3).join("/");
    setIsAttachmentLoading(true);
    try {
      const res = await axios.get(API_GET_EMPLOYEE_FILE, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { filepath },
        responseType: "blob",
      });
      if (res.status === 200) {
        const type = res.data.type || fallbackType;
        const url = URL.createObjectURL(new Blob([res.data], { type }));
        setDocumentDetail({ url, type, name: fileName });
      }
    } catch {
      showError("Unable to open attachment.");
    } finally {
      setIsAttachmentLoading(false);
    }
  };

  const columns: ResponsiveColumn<AnnouncementRow>[] = [
    { id: "title", header: "Title", accessor: "title", truncate: true },
    { id: "description", header: "Description", accessor: "description", truncate: true },
    { id: "venue", header: "Venue", accessor: "venue", truncate: true },
    { id: "mode", header: "Mode", accessor: "mode" },
    { id: "presentedBy", header: "Presented By", accessor: "presentedBy", truncate: true },
    { id: "startDateTime", header: "Start Date Time", render: (row) => moment(row.startDateTime).format("LLL"), nowrap: true },
    { id: "endDateTime", header: "End Date Time", render: (row) => moment(row.endDateTime).format("LLL"), nowrap: true },
    {
      id: "attachment", header: "Attachment", nowrap: true,
      render: (row) => announcementAttachmentName(row.attachment) ? (
        <Button size="sm" variant="muted-outline" startIcon={<Eye size={15} />} disabled={isAttachmentLoading} onClick={() => void viewAttachment(row.attachment!)}>
          View
        </Button>
      ) : "-",
    },
    { id: "status", header: "State", render: (row) => <Badge variant={announcementStatusVariant(row.status)}>{announcementStatusLabel(row.status)}</Badge>, nowrap: true },
  ];

  return <PageShell>
    <div className={styles.page}>  
      <Card className={styles.tabsCard}>
        <Tabs value={activeTabId} onValueChange={(value) => { setActiveTabId(value); setCurrentPage(1); }} idPrefix="announcement-sessions">
          <TabsList className={styles.tabsList}>
            {sessions.map((session: { value: string; label: string }, index: number) =>
              <TabsTrigger key={session.value} value={`tab${index + 1}`}>{session.label}</TabsTrigger>,
            )}
          </TabsList>
          <TabsContent value={activeTabId}>
            {isLoading ? <div className={styles.loading}>Loading announcements...</div> :
              <ResponsiveDataTable<AnnouncementRow>
                aria-label="Announcements table"
                columns={columns}
                data={rows}
                serialNumber
                page={currentPage}
                pageSize={pageSize}
                totalItems={totalRequest}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                pageSizeOptions={[5, 10, 20, 30, 40]}
                totalLabel={<>Total Requests: <strong>{totalRequest}</strong></>}
                emptyState={<div className={styles.empty}>No Record Available</div>}
              />
            }
          </TabsContent>
        </Tabs>
      </Card>
      <button id="open-document-view" type="button" data-bs-toggle="modal" data-bs-target="#staticBackdropDocumentViewer" hidden />
      {documentDetail ? <DocumentViewer url={documentDetail.url} contentType={documentDetail.type} fileName={documentDetail.name} /> : null}
    </div>
  </PageShell>;
}
